/**
 * HR/ผู้บริหาร — รายละเอียดรอบประเมิน (/hr/evaluations/:cycleId).
 * เห็นทุกใบ (ตนเอง/หัวหน้า/เพื่อน) + คะแนนเฉลี่ย · ผู้บริหาร (OWNER) ตัดสินผลที่นี่
 */
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useHrEvaluationDetail, useDecideEval } from '../../hooks';
import { useToast } from '../../components/ui/toast';
import { useAuthStore } from '../../lib/store';
import { EVAL_OUTCOMES } from '@payroll/shared';
import { CheckCircle2, Clock } from 'lucide-react';

const ROLE_TH: Record<string, string> = { self: 'ตนเอง', supervisor: 'หัวหน้า', peer: 'เพื่อนร่วมงาน' };
const DECISION_TH: Record<string, string> = {
  pass: 'ผ่านการประเมิน', pass_conditional: 'ผ่านแบบมีเงื่อนไข', needs_improve: 'ต้องปรับปรุง', fail: 'ไม่ผ่านการประเมิน',
};

interface Assignment { id: string; role: string; status: string; total_score: number | null; outcome: string | null; submitted_at: string | null; evaluator_name: string }
interface Detail {
  cycle: { id: string; subject_name: string; subject_dept: string; title: string; due_date: string; status: string; decision: string | null; decision_note: string | null };
  averages: Record<string, { avg: number; n: number }>;
  assignments: Assignment[];
}

export function HREvaluationDetail() {
  const { cycleId } = useParams<{ cycleId: string }>();
  const { data, isLoading } = useHrEvaluationDetail(cycleId);
  const decide = useDecideEval();
  const toast = useToast();
  const role = useAuthStore((s) => s.profile?.role ?? 'EMPLOYEE');
  const [decision, setDecision] = useState('');
  const [note, setNote] = useState('');

  const d = data as unknown as Detail | undefined;
  if (isLoading || !d) return <AdminLayout title="รายละเอียดการประเมิน"><div className="py-10 text-center text-slate-400 text-sm">กำลังโหลด…</div></AdminLayout>;

  const c = d.cycle;
  const canDecide = role === 'OWNER' && c.status === 'pending_decision';

  async function onDecide() {
    if (!decision) { toast.error('เลือกผลการประเมิน'); return; }
    try {
      await decide.mutateAsync({ cycleId: c.id, decision, note });
      toast.success('บันทึกผลการประเมินแล้ว');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'บันทึกไม่สำเร็จ');
    }
  }

  return (
    <AdminLayout title="รายละเอียดการประเมิน">
      <div className="mb-4">
        <div className="text-lg font-semibold text-slate-800">{c.subject_name}</div>
        <div className="text-sm text-slate-500">{c.subject_dept} · {c.title} · ครบกำหนด {c.due_date}</div>
      </div>

      {/* คะแนนเฉลี่ยต่อบทบาท */}
      <div className="grid grid-cols-3 gap-2 mb-5">
        {(['self', 'supervisor', 'peer'] as const).map((r) => {
          const a = d.averages[r];
          return (
            <div key={r} className="bg-white rounded-xl border border-slate-200 p-3 text-center">
              <div className="text-xs text-slate-400">{ROLE_TH[r]}</div>
              <div className="text-lg font-bold text-slate-800">{a ? a.avg : '—'}</div>
              <div className="text-[11px] text-slate-400">{a ? `${a.n} คน` : 'ยังไม่มี'}</div>
            </div>
          );
        })}
      </div>

      {/* รายการใบประเมิน */}
      <div className="rounded-xl border border-slate-200 overflow-hidden mb-6">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-left">
            <tr>
              <th className="p-3 font-medium">ผู้ประเมิน</th>
              <th className="p-3 font-medium">บทบาท</th>
              <th className="p-3 font-medium">คะแนน</th>
              <th className="p-3 font-medium">สถานะ</th>
            </tr>
          </thead>
          <tbody>
            {d.assignments.map((a) => (
              <tr key={a.id} className="border-t border-slate-100">
                <td className="p-3 text-slate-700">{a.evaluator_name}</td>
                <td className="p-3 text-slate-500">{ROLE_TH[a.role] ?? a.role}</td>
                <td className="p-3 text-slate-700">
                  {a.status === 'submitted' ? a.total_score : '—'}
                  {a.outcome && <span className="text-xs text-slate-400 ml-1">({DECISION_TH[a.outcome] ?? a.outcome})</span>}
                </td>
                <td className="p-3">
                  {a.status === 'submitted' ? (
                    <span className="inline-flex items-center gap-1 text-green-600 text-xs"><CheckCircle2 className="w-3.5 h-3.5" /> ส่งแล้ว</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-slate-400 text-xs"><Clock className="w-3.5 h-3.5" /> รอกรอก</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ผลตัดสิน */}
      {c.decision ? (
        <div className="p-4 rounded-xl bg-green-50 border border-green-200">
          <div className="font-medium text-green-800">ผลการประเมิน: {DECISION_TH[c.decision] ?? c.decision}</div>
          {c.decision_note && <div className="text-sm text-green-700 mt-1">{c.decision_note}</div>}
        </div>
      ) : canDecide ? (
        <div className="p-4 rounded-xl bg-white border border-slate-200">
          <div className="font-medium text-slate-800 mb-2">สรุปผลการประเมิน (ผู้บริหาร)</div>
          <div className="space-y-1.5 mb-3">
            {EVAL_OUTCOMES.map((o) => (
              <label key={o.key} className="flex items-center gap-2 text-sm">
                <input type="radio" name="decision" checked={decision === o.key} onChange={() => setDecision(o.key)} />
                {o.label}
              </label>
            ))}
          </div>
          <textarea
            value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="เหตุผลเพิ่มเติม (ถ้ามี)"
            className="w-full p-2 rounded-lg border border-slate-200 text-sm mb-3"
          />
          <button onClick={onDecide} disabled={decide.isPending} className="w-full py-2.5 rounded-xl bg-slate-900 text-white font-medium disabled:opacity-50">
            {decide.isPending ? 'กำลังบันทึก…' : 'บันทึกผลการประเมิน'}
          </button>
        </div>
      ) : c.status === 'pending_decision' ? (
        <div className="p-3 rounded-xl bg-blue-50 text-blue-700 text-sm">รอผู้บริหารตัดสินผล</div>
      ) : (
        <div className="p-3 rounded-xl bg-amber-50 text-amber-700 text-sm">กำลังเก็บแบบประเมิน — เมื่อครบ HR กดส่งให้ผู้บริหารตัดสิน</div>
      )}
    </AdminLayout>
  );
}
